import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tab, TabList, Tabs } from "@carbon/react";
import {
  GoBackArrow,
  Loader,
  CompanySetupBanner,
  AIProcessingBanner,
  NetworkError,
} from "..";
import PageHeader from "../carbon/PageHeader/PageHeader";
import { useOpportunityCounts } from "../../hooks/useOpportunityDiscovery";
import { CategoryType, FileType } from "../../types";
import { useUser } from "../../context/hooks/useUser";
import { useCompanyProfile } from "../../hooks/useCompany";
import { useUserMaterialCategories } from "../../hooks/useUserMaterialCategories";
import AssignedCategories from "../AssignedCategories/AssignedCategories";
import { OpportunityDiscovery } from "../../containers";
import {
  buildOpportunityTabCards,
  resolveActiveTabKey,
  TabKey,
} from "./opportunityTabCards";
import "./opportunityDashboard.scss";

/**
 * Rebuilt on Carbon (Task 4, Opportunity Discovery + AI Profiling flow): `PageHeader` for the
 * title, and `Tabs`/`TabList`/`Tab` (same plain-chrome pattern `Deliveries.tsx`/
 * `BiddingOpportunities.tsx` use — content rendered separately below, not `TabPanels`) replacing
 * the custom `role="tab"` `OpportunityCard` buttons.
 *
 * The count>0 filtering + always-present "Assigned Categories" tab (`opportunityTabCards.ts`) is
 * ported verbatim — this is the exact shape `e2e/opportunities.spec.ts` pins for a seller whose
 * company has zero opportunities of every type: `TabList` still carries `role="tablist"` (Carbon
 * sets that), `aria-label` is still the dashboard title, each `Tab` still carries `role="tab"`
 * with Carbon's own `aria-selected` management, and with every type count at 0 the list still
 * collapses to the one "Assigned Categories" tab (`OpportunityDiscovery`/`OpportunityFilters`
 * never mount in that case, same as before).
 *
 * `AssignedCategories` and `OpportunityFilters` (nested inside `OpportunityDiscovery`) are now
 * Carbon too (Task 5, the flow's final task) — both hosted inside this shell unchanged in props
 * and behaviour, only their own internal markup moved off Tailwind.
 */
const OpportunityDashboard = () => {
  const { t } = useTranslation();
  const { user } = useUser();

  const { data: companyProfile } = useCompanyProfile(user?.company?.id || "");
  const { data: userMaterialCategories } = useUserMaterialCategories();

  const {
    data: counts,
    isLoading: isLoadingCounts,
    error: countsError,
  } = useOpportunityCounts();

  const [activeTab, setActiveTab] = useState<TabKey>(
    CategoryType.INDIRECT_GOODS
  );

  const hasWebsite = !!companyProfile?.website;
  const hasProductCatalog = companyProfile?.files?.some(
    (file: any) => file.type === FileType.PRODUCT_CATALOG
  );
  const hasUserMaterialCategories =
    !!userMaterialCategories && userMaterialCategories.length > 0;

  const shouldShowSetupBanner =
    !hasUserMaterialCategories && !hasWebsite && !hasProductCatalog;
  const shouldShowProcessingBanner =
    !hasUserMaterialCategories && (hasWebsite || hasProductCatalog);

  if (isLoadingCounts) {
    return (
      <div className="opportunity-dashboard__loading">
        <Loader />
      </div>
    );
  }

  if (countsError) {
    return (
      <NetworkError
        title={t("opportunities.dashboard.error")}
        message={t("opportunities.dashboard.errorDescription")}
      />
    );
  }

  const assignedCategoriesCount = userMaterialCategories?.length ?? 0;
  const tabCards = buildOpportunityTabCards(counts, assignedCategoriesCount, t);
  const resolvedActiveTab = resolveActiveTabKey(tabCards, activeTab);
  const selectedIndex = Math.max(
    tabCards.findIndex((tab) => tab.key === resolvedActiveTab),
    0
  );

  const handleTabChange = ({
    selectedIndex: index,
  }: {
    selectedIndex: number;
  }) => {
    const next = tabCards[index]?.key;
    if (next) setActiveTab(next);
  };

  const renderTabContent = () => {
    if (resolvedActiveTab === "ASSIGNED_CATEGORIES") {
      return <AssignedCategories />;
    }
    return (
      <OpportunityDiscovery
        type={resolvedActiveTab as CategoryType}
        disableQuerySync
      />
    );
  };

  return (
    <div>
      <GoBackArrow />

      <div className="opportunity-dashboard__banners">
        {shouldShowSetupBanner && (
          <CompanySetupBanner companyProfile={companyProfile} />
        )}
        {shouldShowProcessingBanner && <AIProcessingBanner />}
      </div>

      <PageHeader title={t("opportunities.dashboard.title")} />
      <p className="opportunity-dashboard__subtitle">
        {t("opportunities.dashboard.subtitle")}
      </p>

      <Tabs selectedIndex={selectedIndex} onChange={handleTabChange}>
        <TabList aria-label={t("opportunities.dashboard.title")}>
          {tabCards.map((card) => (
            <Tab key={card.key}>
              <span className="opportunity-dashboard__tab">
                <card.Icon size={16} />
                <span>{card.title}</span>
                <span className="opportunity-dashboard__tab-count">
                  {card.count.toLocaleString()}
                </span>
              </span>
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <div className="opportunity-dashboard__tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default OpportunityDashboard;
